// Shared vocabulary for the Detection rules page. Field and check lists
// mirror worker/src/routes/detection-rules.ts (FIELDS, validLeaf); keep them in sync.
import { parseTs } from '../SecurityAlerts/model';

export const SEVERITIES = ['critical', 'high', 'medium', 'low'];
export const SEV_LABEL = { critical: 'CRIT', high: 'HIGH', medium: 'MED', low: 'LOW', info: 'INFO' };
export const SEV_TOKEN = { critical: 'crit', high: 'high', medium: 'med', low: 'low', info: 'low' };
export const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
export const CATEGORIES = [['network', 'Network'], ['process', 'Process'], ['persistence', 'Persistence'], ['vuln', 'Vulnerability']];
export const CATEGORY_LABEL = Object.fromEntries(CATEGORIES);
export const NOISY_AT = 20; // findings in 7 days

// [field, label, kind, noun for sentences, where it comes from, help]
export const FIELDS = [
  ['ports[].port', 'Open port', 'num', 'an open port', 'snapshot', 'Any listening port on the asset. One match is enough.'],
  ['ports[].protocol', 'Port protocol', 'str', 'a port protocol', 'snapshot', 'tcp or udp.'],
  ['ports[].service', 'Port service', 'str', "a port's service name", 'snapshot', 'The service name the agent saw on the port, e.g. ssh.'],
  ['connections[].ip', 'Connection IP', 'ip', 'a connection IP', 'heartbeat', 'Any IP the asset is talking to.'],
  ['connections[].port', 'Connection port', 'num', 'a connection port', 'heartbeat', 'The remote port of an outbound connection.'],
  ['dns[]', 'DNS lookup', 'str', 'a DNS lookup', 'heartbeat', 'Domains the asset looked up. Case-insensitive.'],
  ['process_hashes[].sha256', 'Program hash', 'str', "a running program's SHA-256", 'heartbeat', 'SHA-256 of a running program.'],
  ['process_hashes[].path', 'Program path', 'str', "a running program's path", 'heartbeat', 'Full path of a running program.'],
  ['packages[].name', 'Package name', 'str', 'an installed package', 'snapshot', 'Checked together with other package conditions on the same package.'],
  ['packages[].version', 'Package version', 'str', 'its version', 'snapshot', 'Compared as a version: 2.9 is older than 2.10.'],
  ['packages[].ecosystem', 'Package ecosystem', 'str', 'its ecosystem', 'snapshot', 'deb, rpm, npm, pypi, maven…'],
  ['services[]', 'Service', 'str', 'a service', 'snapshot', 'Installed services by name.'],
  ['system.hostname', 'Hostname', 'str', 'the hostname', 'host', 'Case-insensitive. Use matches regex for patterns.'],
  ['system.os', 'Operating system', 'str', 'the OS', 'host', 'As the agent reports it, e.g. Ubuntu 22.04.'],
].map(([field, label, kind, noun, source, help]) => ({ field, label, kind, noun, source, help }));
export const FIELD = Object.fromEntries(FIELDS.map(f => [f.field, f]));
const OBS_FIELDS = ['connections[].ip', 'dns[]', 'process_hashes[].sha256', 'services[]'];

// A check is what the person picks; it maps to an engine operator plus negate.
// value: 'one' | 'list' | 'none' | 'level' | 'window' | 'count'
export const CHECKS = {
  equals: { label: 'equals', op: 'equals', value: 'one' },
  not_equals: { label: 'does not equal', op: 'equals', negate: true, value: 'one' },
  contains: { label: 'contains', op: 'contains', value: 'one' },
  not_contains: { label: 'does not contain', op: 'contains', negate: true, value: 'one' },
  starts_with: { label: 'starts with', op: 'starts_with', value: 'one' },
  ends_with: { label: 'ends with', op: 'ends_with', value: 'one' },
  regex: { label: 'matches regex', op: 'regex', value: 'one' },
  in_list: { label: 'is one of', op: 'in_list', value: 'list' },
  not_in_list: { label: 'is none of', op: 'in_list', negate: true, value: 'list' },
  new_since_previous: { label: 'is new since the last snapshot', op: 'not_in', value: 'none' },
  ip_in_cidr: { label: 'is in IP range', op: 'ip_in_cidr', value: 'one' },
  gt: { label: 'greater than', op: 'gt', value: 'one' },
  lt: { label: 'less than', op: 'lt', value: 'one' },
  exists: { label: 'is present', op: 'exists', value: 'none' },
  matches_threat_intel: { label: 'is on a MISP list', op: 'matches_threat_intel', value: 'level' },
  matches_threat_data: { label: 'matches threat data', op: 'matches_threat_data', value: 'none' },
  first_seen: { label: 'is seen for the first time', op: 'first_seen', value: 'window' },
  events_in_window: { label: 'appears N+ times in a window', op: 'events_in_window', value: 'count' },
  seen_on_servers: { label: 'is seen on N+ assets', op: 'seen_on_servers', value: 'count' },
};

export function checksFor(field) {
  const f = FIELD[field];
  if (!f) return [];
  const list = f.kind === 'num'
    ? ['equals', 'not_equals', 'in_list', 'not_in_list', 'gt', 'lt', 'exists']
    : ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'regex', 'in_list', 'not_in_list', 'exists'];
  if (f.kind === 'ip') list.push('ip_in_cidr', 'matches_threat_intel');
  if (field === 'packages[].version') list.push('gt', 'lt');
  if (field === 'services[]' || field === 'ports[].port') list.push('new_since_previous');
  if (field === 'packages[].name' || field === 'process_hashes[].sha256') list.push('matches_threat_data');
  if (OBS_FIELDS.includes(field)) list.push('first_seen', 'events_in_window', 'seen_on_servers');
  return list;
}

/** Engine leaf → the check the builder shows. */
export function checkOf(leaf) {
  const op = leaf.operator;
  if (op === 'not_in') return typeof leaf.value === 'string' ? 'new_since_previous' : 'not_in_list';
  if (leaf.negate) return { equals: 'not_equals', contains: 'not_contains', in_list: 'not_in_list' }[op] || op;
  return CHECKS[op] ? op : op;
}

/** Builder leaf ({field, check, value…}) → the API's leaf. */
export function toApiLeaf(l) {
  const c = CHECKS[l.check] || CHECKS.equals;
  const leaf = { field: l.field, operator: c.op, negate: !!c.negate };
  if (c.value === 'one') leaf.value = l.value;
  else if (c.value === 'list') leaf.value = String(l.value || '').split(',').map(s => s.trim()).filter(Boolean);
  else if (c.value === 'level') leaf.value = { threat_level: l.level || 'high' };
  else if (c.value === 'window') leaf.value = { window_minutes: windowMinutes(l) };
  else if (c.value === 'count') leaf.value = { threshold: Number(l.count), window_minutes: windowMinutes(l) };
  if (l.check === 'new_since_previous') leaf.value = l.field === 'ports[].port' ? 'previousPorts' : 'previousServices';
  if (l.check === 'matches_threat_data') leaf.value = { type: l.field === 'process_hashes[].sha256' ? 'hash' : 'cve' };
  return leaf;
}

const UNIT_MIN = { min: 1, h: 60, d: 1440 };
const windowMinutes = (l) => Math.round(Number(l.window || 0) * (UNIT_MIN[l.unit] || 1));
const splitWindow = (m) => (m && m % 1440 === 0 ? [m / 1440, 'd'] : m && m % 60 === 0 ? [m / 60, 'h'] : [m || 60, 'min']);

/** API leaf → builder leaf. */
export function fromApiLeaf(leaf) {
  const check = checkOf(leaf);
  const v = leaf.value;
  const out = { key: Math.random().toString(36).slice(2), field: leaf.field, check, value: '', level: 'high', count: '', window: 60, unit: 'min' };
  if (Array.isArray(v)) out.value = v.join(', ');
  else if (v && typeof v === 'object') {
    if (v.threat_level) out.level = v.threat_level;
    if (v.threshold != null) out.count = String(v.threshold);
    if (v.window_minutes != null) [out.window, out.unit] = splitWindow(v.window_minutes);
  } else if (v != null) out.value = String(v);
  return out;
}

export const newLeaf = (field = 'ports[].port') => ({ key: Math.random().toString(36).slice(2), field, check: checksFor(field)[0], value: '', level: 'high', count: '50', window: 5, unit: 'min' });

// ── sentences ───────────────────────────────────────────────────────────

const fmtWindow = (m) => {
  const [n, u] = splitWindow(m);
  return `${n} ${u === 'min' ? 'min' : u === 'h' ? (n === 1 ? 'hour' : 'hours') : (n === 1 ? 'day' : 'days')}`;
};
const listText = (v) => {
  const a = Array.isArray(v) ? v : [];
  return a.length > 6 ? `${a.slice(0, 6).join(', ')} and ${a.length - 6} more` : a.join(', ');
};

/** One condition in plain words: "an open port is one of 23, 3389". */
export function leafSentence(leaf) {
  const f = FIELD[leaf.field];
  const noun = f?.noun || leaf.field;
  const v = leaf.value;
  const version = leaf.field === 'packages[].version';
  switch (checkOf(leaf)) {
    case 'equals': return `${noun} is ${v}`;
    case 'not_equals': return `${noun} is not ${v}`;
    case 'contains': return `${noun} contains ${v}`;
    case 'not_contains': return `${noun} does not contain ${v}`;
    case 'starts_with': return `${noun} starts with ${v}`;
    case 'ends_with': return `${noun} ends with ${v}`;
    case 'regex': return `${noun} matches /${v}/`;
    case 'in_list': return `${noun} is one of ${listText(v)}`;
    case 'not_in_list': return `${noun} is none of ${listText(v)}`;
    case 'new_since_previous': return `${noun} wasn’t there in the previous snapshot`;
    case 'ip_in_cidr': return `${noun} is in ${v}`;
    case 'gt': return `${noun} is ${version ? 'newer than' : 'greater than'} ${v}`;
    case 'lt': return `${noun} is ${version ? 'older than' : 'less than'} ${v}`;
    case 'exists': return `there is ${noun}`;
    case 'matches_threat_intel': return `${noun} is on a MISP list at ${v?.threat_level || 'high'} or above`;
    case 'matches_threat_data': return `${noun} matches your ${v?.type === 'hash' ? 'hash' : 'CVE'} threat data`;
    case 'first_seen': return `${noun} hasn’t been seen in the previous ${fmtWindow(v?.window_minutes ?? 1440)}`;
    case 'events_in_window': return `the same ${noun.replace(/^an? /, '')} appears ${v?.threshold}+ times within ${fmtWindow(v?.window_minutes ?? 1440)}`;
    case 'seen_on_servers': return `the same ${noun.replace(/^an? /, '')} is seen on ${v?.threshold}+ assets within ${fmtWindow(v?.window_minutes ?? 1440)}`;
    default: return `${leaf.field} ${leaf.operator} ${typeof v === 'object' ? JSON.stringify(v) : v ?? ''}`.trim();
  }
}

const isGroup = (x) => x && Array.isArray(x.conditions);
export function matchSentence(match) {
  const part = (g, top) => {
    const glue = g.logic === 'any' ? ' or ' : ' and ';
    const s = (g.conditions || []).map(c => (isGroup(c) ? part(c, false) : leafSentence(c))).join(glue);
    return top || (g.conditions || []).length < 2 ? s : `(${s})`;
  };
  const body = part(match || { conditions: [] }, true);
  return body ? `Fires when ${body}.` : 'No conditions yet.';
}

/** "Any of these" with a negated or host-only condition matches almost everything. */
export function matchWarning(match) {
  if (!match || match.logic !== 'any') return null;
  const loose = (match.conditions || []).find(c => !isGroup(c) && (c.negate || FIELD[c.field]?.source === 'host'));
  return loose ? `With Any, “${leafSentence(loose)}” on its own is enough, which matches almost every asset.` : null;
}

const SYM = { equals: '=', contains: 'contains', starts_with: 'starts', ends_with: 'ends with', regex: '~', in_list: 'in', not_in: 'not in', ip_in_cidr: 'in', gt: '>', lt: '<', exists: 'exists', matches_threat_intel: 'matches threat intel', matches_threat_data: 'matches threat data', first_seen: 'first seen', events_in_window: '≥', seen_on_servers: 'on ≥' };
export function leafShort(leaf) {
  const v = leaf.value;
  let val = '';
  if (Array.isArray(v)) val = v.length > 7 ? `${v.slice(0, 7).join(', ')}…` : v.join(', ');
  else if (v && typeof v === 'object') val = v.threat_level ? `≥ ${v.threat_level}` : v.threshold ? `${v.threshold} in ${fmtWindow(v.window_minutes ?? 1440)}${leaf.operator === 'seen_on_servers' ? ' assets' : ''}` : v.window_minutes ? fmtWindow(v.window_minutes) : v.type || '';
  else if (typeof v === 'string' && v.startsWith('previous')) val = 'previous snapshot';
  else if (v != null) val = String(v);
  const op = `${leaf.negate ? 'not ' : ''}${SYM[leaf.operator] || leaf.operator}`;
  return `${leaf.field} ${op}${val ? ` ${val}` : ''}`;
}
export const leavesOf = (g) => (g?.conditions || []).flatMap(c => (isGroup(c) ? leavesOf(c) : [c]));
export const matchShort = (m) => leavesOf(m).map(leafShort).join(' · ');

// ── time ────────────────────────────────────────────────────────────────

export function lastFired(iso) {
  const d = parseTs(iso);
  if (!d) return 'Never';
  const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  const hm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (days <= 0) return `Today ${hm}`;
  if (days === 1) return `Yesterday ${hm}`;
  if (days > 30) return '30+ days ago';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
export const shortDate = (iso) => {
  const d = parseTs(iso);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
};
export const dayLabel = (daysAgo) => {
  if (daysAgo === 0) return 'Today';
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
export const attackUrl = (t) => `https://attack.mitre.org/techniques/${String(t).replace('.', '/')}/`;
